<template>
  <section class="agenda-page clipr-theme twitch-theme">
    <header class="agenda-page-header">
      <span v-if="!loading && !loadError" class="agenda-page-range">{{ calendarRangeLabel }}</span>
      <div v-if="!loading && !loadError" class="agenda-status-legend">
        <span class="agenda-status-legend-item">
          <span class="agenda-status-legend-marker"></span>
          {{ tt("agenda.scheduled", "Programado") }}
        </span>
        <span class="agenda-status-legend-item">
          <span class="agenda-status-legend-marker agenda-status-legend-marker--published"></span>
          {{ tt("agenda.published", "Publicado") }}
        </span>
      </div>
    </header>

    <div v-if="!loading && !loadError" class="agenda-mobile">
      <div class="agenda-mobile-view-switch" role="tablist" :aria-label="tt('agenda.view', 'Vista de agenda')">
        <button
          type="button"
          class="agenda-mobile-view-button"
          :class="{ 'is-active': mobileAgendaView === 'agenda' }"
          :aria-selected="mobileAgendaView === 'agenda'"
          role="tab"
          @click="mobileAgendaView = 'agenda'"
        >
          <i class="ti-view-list-alt" aria-hidden="true"></i>
          {{ tt("agenda.agendaView", "Agenda") }}
        </button>
        <button
          type="button"
          class="agenda-mobile-view-button"
          :class="{ 'is-active': mobileAgendaView === 'month' }"
          :aria-selected="mobileAgendaView === 'month'"
          role="tab"
          @click="mobileAgendaView = 'month'"
        >
          <i class="ti-calendar" aria-hidden="true"></i>
          {{ tt("agenda.monthView", "Mes") }}
        </button>
      </div>

      <div v-if="mobileAgendaView === 'agenda'" class="agenda-mobile-list" role="list">
        <article v-for="day in mobileAgendaDays" :key="day.isoDate" class="agenda-mobile-list-day" role="listitem">
          <header class="agenda-mobile-list-day-header">
            <time :datetime="day.isoDate">{{ day.ariaLabel }}</time>
            <span>{{ day.events.length }}</span>
          </header>
          <div class="agenda-mobile-list-events">
            <button
              v-for="event in day.events"
              :key="event.id"
              type="button"
              class="agenda-mobile-event"
              :class="{ 'agenda-mobile-event--published': event.isPublished }"
              @click.stop="openClipDetails(event)"
            >
              <span class="agenda-mobile-event-title">{{ event.title }}</span>
              <time class="agenda-mobile-event-time">{{ event.postingTime }}</time>
              <span class="agenda-event-socials" aria-label="Redes sociales">
                <i v-for="platform in getEventSocialPlatforms(event)" :key="platform" :class="getSocialIcon(platform)" aria-hidden="true"></i>
              </span>
            </button>
          </div>
        </article>
        <div v-if="mobileAgendaDays.length === 0" class="agenda-mobile-empty">
          <i class="ti-calendar" aria-hidden="true"></i>
          {{ tt("agenda.noEvents", "No hay clips programados en este período.") }}
        </div>
      </div>

      <div v-else class="agenda-mobile-month">
        <div class="agenda-mobile-month-weekdays" aria-hidden="true">
          <span v-for="weekday in weekdayLabels" :key="weekday">{{ weekday }}</span>
        </div>
        <div class="agenda-mobile-month-grid" role="grid">
          <button
            v-for="day in calendarDays"
            :key="day.isoDate"
            type="button"
            class="agenda-mobile-month-day"
            :class="{
              'is-outside-month': !day.isCurrentMonth,
              'is-today': day.isToday,
              'is-selected': day.isoDate === selectedMobileAgendaDate,
              'has-events': day.events.length > 0,
            }"
            :aria-label="day.ariaLabel"
            :aria-pressed="day.isoDate === selectedMobileAgendaDate"
            role="gridcell"
            @click="selectMobileAgendaDay(day)"
          >
            <span class="agenda-mobile-month-day-number">{{ day.dayNumber }}</span>
            <span v-if="day.events.length" class="agenda-mobile-month-day-events" aria-hidden="true">
              <span
                v-for="event in day.events.slice(0, 3)"
                :key="event.id"
                class="agenda-mobile-month-day-dot"
                :class="{ 'is-published': event.isPublished }"
              ></span>
              <span v-if="day.events.length > 3" class="agenda-mobile-month-day-count">+{{ day.events.length - 3 }}</span>
            </span>
          </button>
        </div>

        <section v-if="mobileSelectedAgendaDay" class="agenda-mobile-day-detail" :aria-label="mobileSelectedAgendaDay.ariaLabel">
          <header class="agenda-mobile-day-detail-header">
            <time :datetime="mobileSelectedAgendaDay.isoDate">{{ mobileSelectedAgendaDay.ariaLabel }}</time>
            <span>{{ mobileSelectedAgendaDay.events.length }}</span>
          </header>
          <div v-if="mobileSelectedAgendaDay.events.length" class="agenda-mobile-list-events">
            <button
              v-for="event in mobileSelectedAgendaDay.events"
              :key="event.id"
              type="button"
              class="agenda-mobile-event"
              :class="{ 'agenda-mobile-event--published': event.isPublished }"
              @click.stop="openClipDetails(event)"
            >
              <span class="agenda-mobile-event-title">{{ event.title }}</span>
              <time class="agenda-mobile-event-time">{{ event.postingTime }}</time>
              <span class="agenda-event-socials" aria-label="Redes sociales">
                <i v-for="platform in getEventSocialPlatforms(event)" :key="platform" :class="getSocialIcon(platform)" aria-hidden="true"></i>
              </span>
            </button>
          </div>
          <p v-else class="agenda-mobile-day-detail-empty">
            {{ tt("agenda.noEventsForDay", "No hay clips programados este día.") }}
          </p>
        </section>
      </div>
    </div>

    <div v-if="!loading && !loadError" class="agenda-desktop-view-switch" role="tablist" :aria-label="tt('agenda.view', 'Vista de agenda')">
      <button
        type="button"
        class="agenda-desktop-view-button"
        :class="{ 'is-active': desktopAgendaView === 'month' }"
        :aria-selected="desktopAgendaView === 'month'"
        role="tab"
        @click="desktopAgendaView = 'month'"
      >
        <i class="ti-calendar" aria-hidden="true"></i>
        {{ tt("agenda.monthView", "Mes") }}
      </button>
      <button
        type="button"
        class="agenda-desktop-view-button"
        :class="{ 'is-active': desktopAgendaView === 'agenda' }"
        :aria-selected="desktopAgendaView === 'agenda'"
        role="tab"
        @click="desktopAgendaView = 'agenda'"
      >
        <i class="ti-view-list-alt" aria-hidden="true"></i>
        {{ tt("agenda.agendaView", "Agenda") }}
      </button>
    </div>

    <div class="agenda-calendar agenda-calendar--desktop">
      <div
        v-if="!loading && !loadError && desktopAgendaView === 'month'"
        class="agenda-weekdays"
        role="row"
      >
        <span v-for="weekday in weekdayLabels" :key="weekday" role="columnheader">
          {{ weekday }}
        </span>
      </div>

      <div v-if="loading" class="agenda-state" role="status">
        <i class="ti-reload agenda-state-icon"></i>
        {{ tt("common.loading", "Cargando...") }}
      </div>

      <div v-else-if="loadError" class="agenda-state agenda-state--error" role="alert">
        <i class="ti-alert agenda-state-icon"></i>
        {{ loadError }}
      </div>

      <div v-else-if="desktopAgendaView === 'month'" class="agenda-desktop-month-layout">
        <div class="agenda-days" role="grid">
        <article
          v-for="day in calendarDays"
          :key="day.isoDate"
          class="agenda-day"
          :class="{
            'agenda-day--outside-month': !day.isCurrentMonth,
            'agenda-day--today': day.isToday,
            'agenda-day--selected': day.isoDate === selectedDesktopAgendaDate,
          }"
          role="gridcell"
          :aria-label="day.ariaLabel"
          @click="selectDesktopAgendaDay(day)"
        >
          <header class="agenda-day-header">
            <div class="agenda-day-header-left">
              <time :datetime="day.isoDate" class="agenda-day-number">{{ day.dayNumber }}</time>
              <span v-if="day.isToday" class="agenda-day-today-tag">
                {{ tt("common.today", "Hoy") }}
              </span>
            </div>
            <span v-if="day.showMonthLabel" class="agenda-day-month">
              {{ day.monthLabel }}
            </span>
          </header>

          <div class="agenda-day-events">
            <button
              v-for="event in day.events.slice(0, 2)"
              :key="event.id"
              type="button"
              class="agenda-event"
              :class="{ 'agenda-event--published': event.isPublished }"
              :title="event.title"
              @click.stop="openClipDetails(event)"
            >
              <span class="agenda-event-title">{{ event.title }}</span>
              <span class="agenda-event-time">{{ event.postingTime }}</span>
              <span class="agenda-event-socials" aria-label="Redes sociales">
                <i v-for="platform in getEventSocialPlatforms(event)" :key="platform" :class="getSocialIcon(platform)" aria-hidden="true"></i>
              </span>
            </button>
            <button
              v-if="day.events.length > 2"
              type="button"
              class="agenda-day-more-events"
              @click.stop="selectDesktopAgendaDay(day)"
            >
              +{{ day.events.length - 2 }} {{ tt("agenda.more", "más") }}
            </button>
          </div>
        </article>
      </div>
      <aside v-if="desktopSelectedAgendaDay" class="agenda-desktop-day-detail">
        <header class="agenda-desktop-day-detail-header">
          <time :datetime="desktopSelectedAgendaDay.isoDate">{{ desktopSelectedAgendaDay.ariaLabel }}</time>
          <button type="button" :aria-label="tt('common.close', 'Cerrar')" @click="clearDesktopAgendaDay">
            <i class="ti-close" aria-hidden="true"></i>
          </button>
        </header>
        <div v-if="desktopSelectedAgendaDay.events.length" class="agenda-desktop-day-detail-events">
          <button
            v-for="event in desktopSelectedAgendaDay.events"
            :key="event.id"
            type="button"
            class="agenda-desktop-day-detail-event"
            :class="{ 'agenda-desktop-day-detail-event--published': event.isPublished }"
            @click.stop="openClipDetails(event)"
          >
            <span>{{ event.title }}</span>
            <time>{{ event.postingTime }}</time>
            <span class="agenda-event-socials" aria-label="Redes sociales">
              <i v-for="platform in getEventSocialPlatforms(event)" :key="platform" :class="getSocialIcon(platform)" aria-hidden="true"></i>
            </span>
          </button>
        </div>
        <p v-else class="agenda-desktop-day-detail-empty">
          {{ tt("agenda.noEventsForDay", "No hay clips programados este día.") }}
        </p>
      </aside>
    </div>
    <div v-else class="agenda-desktop-list" role="list">
      <article v-for="day in agendaDaysWithEvents" :key="day.isoDate" class="agenda-desktop-list-day" role="listitem">
        <header>
          <time :datetime="day.isoDate">{{ day.ariaLabel }}</time>
          <span>{{ day.events.length }}</span>
        </header>
        <div class="agenda-desktop-list-events">
          <button
            v-for="event in day.events"
            :key="event.id"
            type="button"
            class="agenda-desktop-list-event"
            :class="{ 'agenda-desktop-list-event--published': event.isPublished }"
            @click.stop="openClipDetails(event)"
          >
            <span>{{ event.title }}</span>
            <time>{{ event.postingTime }}</time>
            <span class="agenda-event-socials" aria-label="Redes sociales">
              <i v-for="platform in getEventSocialPlatforms(event)" :key="platform" :class="getSocialIcon(platform)" aria-hidden="true"></i>
            </span>
          </button>
        </div>
      </article>
      <div v-if="agendaDaysWithEvents.length === 0" class="agenda-mobile-empty">
        <i class="ti-calendar" aria-hidden="true"></i>
        {{ tt("agenda.noEvents", "No hay clips programados en este período.") }}
      </div>
    </div>
    </div>

    <body-portal>
      <div v-if="clipDetailsModalOpen" class="agenda-modal-backdrop" @click="closeClipDetails">
        <div class="agenda-modal" role="dialog" aria-modal="true" @click.stop>
          <header class="agenda-modal-header">
            <div>
              <h2>{{ tt("twitchPostAdmin.previewTitle", "Preview de clip programado") }}</h2>
              <p>{{ selectedClipDetails?.title || tt("twitchPostAdmin.untitledClip", "Clip sin título") }}</p>
            </div>
            <button type="button" aria-label="Cerrar" @click="closeClipDetails">
              <i class="ti-close" aria-hidden="true"></i>
            </button>
          </header>
          <div class="agenda-modal-body">
            <div v-if="clipDetailsLoading" class="agenda-modal-state">
              <i class="ti-reload rotating"></i>
              {{ tt("common.loading", "Cargando...") }}
            </div>
            <div v-else-if="clipDetailsError" class="agenda-modal-error" role="alert">{{ clipDetailsError }}</div>
            <template v-else-if="clipDetails">
              <video
                v-if="clipDetails.videoUrl"
                class="agenda-modal-video"
                :src="clipDetails.videoUrl"
                controls
                playsinline
                preload="metadata"
              ></video>
              <div class="agenda-modal-meta">
                <p><strong>{{ tt("twitchPostAdmin.status", "Estado") }}:</strong> {{ clipDetails.status || "N/A" }}</p>
                <p><strong>{{ tt("twitchPostAdmin.time", "Hora") }}:</strong> {{ selectedClipDetails?.postingTime || "N/A" }}</p>
                <div v-if="clipDetailsSocialUrls.length" class="agenda-modal-socials">
                  <strong>{{ tt("twitchPostAdmin.publishedUrls", "URLs publicadas") }}:</strong>
                  <a
                    v-for="social in clipDetailsSocialUrls"
                    :key="social.platform"
                    :href="social.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i :class="getSocialIcon(social.platform)" aria-hidden="true"></i>
                    {{ social.label }}
                  </a>
                </div>
              </div>
              <div class="agenda-modal-actions">
                <button
                  v-if="!clipDetailsManageMode"
                  type="button"
                  class="agenda-modal-manage"
                  @click="clipDetailsManageMode = true"
                >
                  <i class="ti-settings" aria-hidden="true"></i>
                  {{ tt("common.manage", "Administrar") }}
                </button>
                <button
                  v-else-if="canCancelClipDetails"
                  type="button"
                  class="agenda-modal-cancel"
                  :disabled="clipDetailsCancelling"
                  @click="cancelClipDetails"
                >
                  <i v-if="clipDetailsCancelling" class="ti-reload rotating" aria-hidden="true"></i>
                  <i v-else class="ti-na" aria-hidden="true"></i>
                  {{ clipDetailsCancelling ? tt("twitchPostAdmin.cancellingUpload", "CANCELANDO...") : tt("twitchPostAdmin.cancelUpload", "CANCELAR SUBIDA") }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </body-portal>
  </section>
</template>

<script>
import { fetchTwitchPostingTimeline } from "@/api/twitch";
import { apiRequest, readJsonOrThrow } from "@/api/client";
import { supabase } from "@/supabase";

const POSTING_TIMEZONES = {
  PST: "Etc/GMT+8",
  MST: "Etc/GMT+7",
  CST: "Etc/GMT+6",
  EST: "Etc/GMT+5",
  UTC: "UTC",
};

const POSTING_STATUSES = new Set([
  "5_scheduled_to_post",
  "posted",
  "6_posted",
  "7_posted",
]);

const PUBLISHED_STATUSES = new Set(["posted", "6_posted", "7_posted"]);

function formatLocalDateToIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

async function fetchClipPreview(session, clipId) {
  const response = await apiRequest(
    `/momo/twitch/clips/${encodeURIComponent(clipId)}/schedule-preview`,
    { session, includeContentType: false }
  );
  return readJsonOrThrow(response, "Failed to fetch clip preview");
}

export default {
  name: "Agenda",
  data() {
    return {
      rawEvents: [],
      userTimezone: "",
      loading: true,
      loadError: "",
      mobileAgendaView: "agenda",
      selectedMobileAgendaDate: "",
      desktopAgendaView: "month",
      selectedDesktopAgendaDate: "",
      clipDetailsModalOpen: false,
      clipDetailsLoading: false,
      clipDetailsError: "",
      selectedClipDetails: null,
      clipDetails: null,
      clipDetailsManageMode: false,
      clipDetailsCancelling: false,
    };
  },
  computed: {
    locale() {
      return this.$i18n?.locale === "en" ? "en-US" : "es-ES";
    },
    timezone() {
      return POSTING_TIMEZONES[this.userTimezone];
    },
    today() {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: this.timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date()).split("-").map(Number);
      return new Date(parts[0], parts[1] - 1, parts[2]);
    },
    calendarStart() {
      const twoWeeksAgo = addDays(this.today, -14);
      const mondayOffset = (twoWeeksAgo.getDay() + 6) % 7;
      return addDays(twoWeeksAgo, -mondayOffset);
    },
    calendarEvents() {
      return this.rawEvents
        .filter((event) => event.clip_id && event.posting_at && POSTING_STATUSES.has(String(event.status || "").toLowerCase()))
        .map((event) => {
          const postingAt = new Date(event.posting_at);
          const postingDate = new Intl.DateTimeFormat("en-CA", {
            timeZone: this.timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(postingAt);
          const postingTime = new Intl.DateTimeFormat(this.locale, {
            timeZone: this.timezone,
            hour: "2-digit",
            minute: "2-digit",
          }).format(postingAt);
          const status = String(event.status || "").toLowerCase();
          const clipId = String(event.clip_id);
          const sourceUsername = String(event.source_username || "").trim();
          const title = String(event.title || "").trim() || (sourceUsername ? `@${sourceUsername}` : `Clip #${clipId.slice(0, 8)}`);

          return {
            id: `${clipId}-${event.posting_at}`,
            date: postingDate,
            postingTime,
            title,
            isPublished: PUBLISHED_STATUSES.has(status),
            outputUrls: event.output_urls,
            status: event.status,
            clipId,
            postingAt: event.posting_at,
          };
        })
        .sort((first, second) => first.date.localeCompare(second.date) || first.postingTime.localeCompare(second.postingTime));
    },
    calendarDays() {
      const currentMonth = this.today.getMonth();
      const todayIso = formatLocalDateToIso(this.today);

      return Array.from({ length: 35 }, (_, index) => {
        const date = addDays(this.calendarStart, index);
        const isoDate = formatLocalDateToIso(date);
        const monthLabel = new Intl.DateTimeFormat(this.locale, { month: "short" })
          .format(date)
          .replace(".", "")
          .toUpperCase();

        return {
          isoDate,
          dayNumber: date.getDate(),
          monthLabel,
          showMonthLabel: date.getDate() === 1,
          isCurrentMonth: date.getMonth() === currentMonth,
          isToday: isoDate === todayIso,
          events: this.calendarEvents.filter((event) => event.date === isoDate),
          ariaLabel: new Intl.DateTimeFormat(this.locale, {
            weekday: "long",
            day: "numeric",
            month: "long",
          }).format(date),
        };
      });
    },
    calendarRangeLabel() {
      const end = addDays(this.calendarStart, 34);
      const monthFormatter = new Intl.DateTimeFormat(this.locale, { month: "long" });
      const startMonth = monthFormatter.format(this.calendarStart);
      const endMonth = monthFormatter.format(end);
      return startMonth === endMonth
        ? startMonth.toUpperCase()
        : `${startMonth.toUpperCase()} — ${endMonth.toUpperCase()}`;
    },
    mobileAgendaDays() {
      return this.agendaDaysWithEvents;
    },
    agendaDaysWithEvents() {
      return this.calendarDays.filter((day) => day.events.length > 0);
    },
    mobileSelectedAgendaDay() {
      return this.calendarDays.find((day) => day.isoDate === this.selectedMobileAgendaDate);
    },
    desktopSelectedAgendaDay() {
      return this.calendarDays.find((day) => day.isoDate === this.selectedDesktopAgendaDate);
    },
    weekdayLabels() {
      return Array.from({ length: 7 }, (_, index) => new Intl.DateTimeFormat(this.locale, {
        weekday: "short",
      })
        .format(new Date(2024, 0, 1 + index))
        .replace(".", "")
        .toUpperCase());
    },
    clipDetailsSocialUrls() {
      return this.extractSocialUrls(this.clipDetails?.outputUrls);
    },
    canCancelClipDetails() {
      return String(this.clipDetails?.status || this.selectedClipDetails?.status || "").toLowerCase() === "5_scheduled_to_post";
    },
  },
  async mounted() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error(this.tt("agenda.loginRequired", "Debes iniciar sesión para ver tu agenda."));
      }

      const payload = await fetchTwitchPostingTimeline(session);
      const userTimezone = String(payload.use_timezone || "").trim();
      if (!POSTING_TIMEZONES[userTimezone]) {
        throw new Error(`Zona horaria de agenda no soportada: ${userTimezone}`);
      }
      this.userTimezone = userTimezone;
      this.rawEvents = Array.isArray(payload.events) ? payload.events : [];
      this.selectedMobileAgendaDate = formatLocalDateToIso(this.today);
    } catch (error) {
      this.loadError = error.message;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    selectMobileAgendaDay(day) {
      this.selectedMobileAgendaDate = day.isoDate;
    },
    selectDesktopAgendaDay(day) {
      this.selectedDesktopAgendaDate = day.isoDate;
    },
    clearDesktopAgendaDay() {
      this.selectedDesktopAgendaDate = "";
    },
    normalizeOutputUrls(value) {
      if (!value) return {};
      if (typeof value === "object" && !Array.isArray(value)) return value;
      if (typeof value !== "string") return {};
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch (error) {
        return {};
      }
    },
    getEventSocialPlatforms(event) {
      return this.extractSocialUrls(event?.outputUrls).map((social) => social.platform);
    },
    getSocialIcon(platform) {
      const icons = {
        instagram: "fa-brands fa-instagram",
        tiktok: "fa-brands fa-tiktok",
        youtube: "fa-brands fa-youtube",
        facebook: "fa-brands fa-facebook",
        linkedin: "fa-brands fa-linkedin",
        x: "fa-brands fa-x-twitter",
        twitter: "fa-brands fa-x-twitter",
      };
      return icons[String(platform || "").toLowerCase()] || "fa-solid fa-link";
    },
    extractSocialUrls(value) {
      const urls = this.normalizeOutputUrls(value);
      return Object.keys(urls)
        .filter((platform) => typeof urls[platform] === "string" && urls[platform].startsWith("http"))
        .map((platform) => ({
          platform,
          label: platform.charAt(0).toUpperCase() + platform.slice(1),
          url: urls[platform],
        }));
    },
    async openClipDetails(event) {
      if (!event?.clipId) return;
      this.clipDetailsModalOpen = true;
      this.clipDetailsLoading = true;
      this.clipDetailsError = "";
      this.selectedClipDetails = event;
      this.clipDetails = null;
      this.clipDetailsManageMode = false;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("agenda.loginRequired", "Debes iniciar sesión para ver tu agenda."));
        }
        const payload = await fetchClipPreview(session, event.clipId);
        const clip = payload?.clip || {};
        this.clipDetails = {
          videoUrl: clip.rendered_video_url || "",
          status: clip.status || event.status,
          outputUrls: clip.output_urls || event.outputUrls,
        };
      } catch (error) {
        this.clipDetailsError = error?.message || "No fue posible cargar los detalles del clip.";
      } finally {
        this.clipDetailsLoading = false;
      }
    },
    closeClipDetails() {
      if (this.clipDetailsCancelling) return;
      this.clipDetailsModalOpen = false;
      this.clipDetailsLoading = false;
      this.clipDetailsError = "";
      this.selectedClipDetails = null;
      this.clipDetails = null;
      this.clipDetailsManageMode = false;
    },
    async cancelClipDetails() {
      const clipId = this.selectedClipDetails?.clipId;
      if (!clipId || !this.canCancelClipDetails) return;
      this.clipDetailsCancelling = true;
      this.clipDetailsError = "";
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("Sesion no valida");
        const response = await apiRequest(`/momo/twitch/clips/${encodeURIComponent(clipId)}`, {
          method: "PATCH",
          session,
          body: {
            status: "4_rendered",
            posting_at: null,
          },
        });
        await readJsonOrThrow(response, "No se pudo cancelar la subida");
        this.rawEvents = this.rawEvents.filter((event) => String(event.clip_id) !== String(clipId));
        this.clipDetailsCancelling = false;
        this.closeClipDetails();
      } catch (error) {
        this.clipDetailsError = error?.message || "No se pudo cancelar la subida";
      } finally {
        this.clipDetailsCancelling = false;
      }
    },
  },
};
</script>

<style scoped>
.agenda-page {
  max-width: 1440px;
  margin: 0 auto;
  padding: 8px 24px 40px;
}

.agenda-page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin: 0 0 22px;
}

.agenda-page-header h1 {
  margin: 0;
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.agenda-page-header p {
  margin: 5px 0 0;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 14px;
}

.agenda-page-range {
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
}

.agenda-status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 11px;
  font-weight: 700;
}

.agenda-status-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.agenda-status-legend-marker {
  width: 18px;
  height: 10px;
  border: 1px solid rgba(96, 165, 250, 0.5);
  border-left: 4px solid #60a5fa;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.16);
}

.agenda-status-legend-marker--published {
  border-color: rgba(74, 222, 128, 0.5);
  border-left-color: #4ade80;
  background: rgba(34, 197, 94, 0.14);
}

.agenda-calendar {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.54);
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.22);
}

.agenda-weekdays,
.agenda-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.agenda-weekdays {
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.7);
}

.agenda-weekdays span {
  padding: 12px 8px;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: center;
}

.agenda-day {
  display: flex;
  min-width: 0;
  min-height: 150px;
  flex-direction: column;
  padding: 10px;
  border-right: 1px solid rgba(148, 163, 184, 0.12);
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(30, 41, 59, 0.36);
  transition: background 180ms ease, opacity 180ms ease;
}

.agenda-day:nth-child(7n) {
  border-right: 0;
}

.agenda-day:nth-last-child(-n + 7) {
  border-bottom: 0;
}

.agenda-day--outside-month {
  background: rgba(15, 23, 42, 0.28);
  opacity: 0.58;
}

.agenda-day--today {
  background: rgba(124, 58, 237, 0.16);
  box-shadow: inset 0 0 0 1px rgba(167, 139, 250, 0.52);
}

.agenda-day-header {
  display: flex;
  min-height: 26px;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.agenda-day-header-left {
  display: flex;
  align-items: center;
  gap: 5px;
}

.agenda-day-number {
  display: inline-flex;
  width: 26px;
  height: 26px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 13px;
  font-weight: 700;
}

.agenda-day--today .agenda-day-number {
  background: #8b5cf6;
  color: #fff;
}

.agenda-day-today-tag {
  padding: 3px 5px;
  border: 1px solid rgba(196, 181, 253, 0.42);
  border-radius: 4px;
  background: rgba(139, 92, 246, 0.2);
  color: #ddd6fe;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
}

.agenda-day-month {
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.agenda-day-events {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
  margin-top: 8px;
}

.agenda-event {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(28px, auto);
  align-items: center;
  min-width: 0;
  gap: 5px;
  padding: 5px 6px;
  width: 100%;
  border: 1px solid rgba(96, 165, 250, 0.26);
  border-left: 4px solid #60a5fa;
  border-radius: 5px;
  background: rgba(59, 130, 246, 0.14);
  color: #dbeafe;
  font-size: 11px;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
}

.agenda-event--published {
  border-color: rgba(74, 222, 128, 0.24);
  border-left-color: #4ade80;
  background: rgba(34, 197, 94, 0.12);
  color: #dcfce7;
}

.agenda-event-time {
  flex: 0 0 auto;
  color: currentColor;
  font-weight: 700;
  opacity: 0.82;
}

.agenda-event-title {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.agenda-event-socials {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  min-width: 28px;
  color: currentColor;
  font-size: 12px;
}

.agenda-event-socials i {
  flex: 0 0 auto;
}

.agenda-state {
  display: flex;
  min-height: 360px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 14px;
}

.agenda-state--error {
  color: #fca5a5;
}

.agenda-state-icon {
  font-size: 18px;
}

.agenda-desktop-view-switch {
  display: flex;
  width: fit-content;
  margin: 0 auto 14px;
  gap: 4px;
  padding: 4px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.7);
}

.agenda-desktop-view-button {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.agenda-desktop-view-button.is-active {
  background: rgba(139, 92, 246, 0.25);
  color: #f5f3ff;
}

.agenda-desktop-month-layout {
  display: flex;
  flex-direction: column;
}

.agenda-day {
  cursor: pointer;
}

.agenda-day--selected {
  background: rgba(139, 92, 246, 0.24);
  box-shadow: inset 0 0 0 2px rgba(167, 139, 250, 0.72);
}

.agenda-day-more-events {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: #c4b5fd;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.agenda-desktop-day-detail {
  padding: 14px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.5);
}

.agenda-desktop-day-detail-header,
.agenda-desktop-list-day > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 700;
  text-transform: capitalize;
}

.agenda-desktop-day-detail-header button {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
  cursor: pointer;
}

.agenda-desktop-day-detail-events,
.agenda-desktop-list-events {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.agenda-desktop-day-detail-event,
.agenda-desktop-list-event {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(28px, auto);
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(96, 165, 250, 0.3);
  border-left: 4px solid #60a5fa;
  border-radius: 9px;
  background: rgba(59, 130, 246, 0.12);
  color: #dbeafe;
  font-size: 13px;
  width: 100%;
  border-width: 1px;
  text-align: left;
  cursor: pointer;
}

.agenda-desktop-day-detail-event--published,
.agenda-desktop-list-event--published {
  border-color: rgba(74, 222, 128, 0.3);
  border-left-color: #4ade80;
  background: rgba(34, 197, 94, 0.1);
  color: #dcfce7;
}

.agenda-desktop-day-detail-event time,
.agenda-desktop-list-event time {
  font-weight: 700;
}

.agenda-desktop-day-detail-event span,
.agenda-desktop-list-event span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda-desktop-day-detail-event small,
.agenda-desktop-list-event small {
  font-size: 11px;
  opacity: 0.78;
}

.agenda-desktop-day-detail-empty {
  margin: 12px 0 0;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 13px;
}

.agenda-desktop-list {
  padding: 14px;
}

.agenda-desktop-list-day + .agenda-desktop-list-day {
  margin-top: 16px;
}

.agenda-desktop-list-day > header > span {
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(139, 92, 246, 0.24);
  color: #ddd6fe;
  font-size: 11px;
}

.agenda-desktop-list .agenda-mobile-empty {
  padding: 48px 16px;
  color: var(--clipr-text-muted, #94a3b8);
  font-size: 13px;
  text-align: center;
}

.agenda-mobile {
  display: none;
}

.agenda-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(2, 6, 23, 0.76);
  backdrop-filter: blur(8px);
}

.agenda-modal {
  width: min(100%, 720px);
  max-height: calc(100vh - 40px);
  overflow: auto;
  border: 1px solid var(--clipr-border-color, rgba(196, 181, 253, 0.35));
  border-radius: 16px;
  background: var(--clipr-bg-card, #12131b);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45), 0 0 24px rgba(168, 85, 247, 0.24);
}

.agenda-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

.agenda-modal-header h2 {
  margin: 0;
  color: var(--clipr-text-primary, #f8fafc);
  font-size: 18px;
}

.agenda-modal-header p {
  margin: 4px 0 0;
  overflow: hidden;
  color: var(--clipr-text-secondary, #cbd5e1);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda-modal-header button {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--clipr-text-secondary, #cbd5e1);
  cursor: pointer;
}

.agenda-modal-body {
  padding: 20px;
}

.agenda-modal-state,
.agenda-modal-error {
  padding: 36px 12px;
  color: var(--clipr-text-secondary, #cbd5e1);
  text-align: center;
}

.agenda-modal-error {
  color: #fca5a5;
}

.agenda-modal-video {
  display: block;
  width: 100%;
  max-width: 360px;
  aspect-ratio: 9 / 16;
  height: auto;
  max-height: 640px;
  margin: 0 auto;
  object-fit: cover;
  border-radius: 10px;
  background: #020617;
}

.agenda-modal-meta {
  margin-top: 16px;
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 13px;
}

.agenda-modal-meta p {
  margin: 0 0 8px;
}

.agenda-modal-socials {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.agenda-modal-socials a {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--clipr-purple-light, #c4b5fd);
}

.agenda-modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.agenda-modal-manage,
.agenda-modal-cancel {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.agenda-modal-manage {
  background: var(--clipr-purple-gradient, linear-gradient(135deg, #7e22ce, #a855f7));
}

.agenda-modal-cancel {
  background: #b91c1c;
}

.agenda-modal-cancel:disabled {
  cursor: wait;
  opacity: 0.7;
}

@media (max-width: 900px) {
  .agenda-page {
    padding-right: 12px;
    padding-left: 12px;
  }

  .agenda-day {
    min-height: 120px;
    padding: 7px 5px;
  }

  .agenda-event {
    padding: 4px;
  }

  .agenda-event-time {
    display: none;
  }
}

@media (max-width: 600px) {
  .agenda-page {
    padding: 8px 12px 28px;
  }

  .agenda-page-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
  }

  .agenda-calendar--desktop {
    display: none;
  }

  .agenda-desktop-view-switch {
    display: none;
  }

  .agenda-mobile {
    display: block;
  }

  .agenda-mobile-view-switch {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    margin-bottom: 12px;
    padding: 4px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 12px;
    background: rgba(15, 23, 42, 0.7);
  }

  .agenda-mobile-view-button {
    display: inline-flex;
    min-height: 40px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--clipr-text-muted, #94a3b8);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  .agenda-mobile-view-button.is-active {
    background: rgba(139, 92, 246, 0.25);
    color: #f5f3ff;
  }

  .agenda-mobile-list,
  .agenda-mobile-month {
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 14px;
    background: rgba(15, 23, 42, 0.54);
    overflow: hidden;
  }

  .agenda-mobile-list-day + .agenda-mobile-list-day {
    border-top: 1px solid rgba(148, 163, 184, 0.14);
  }

  .agenda-mobile-list-day-header,
  .agenda-mobile-day-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    color: #e2e8f0;
    font-size: 13px;
    font-weight: 700;
    text-transform: capitalize;
  }

  .agenda-mobile-list-day-header > span,
  .agenda-mobile-day-detail-header > span {
    display: inline-flex;
    min-width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba(139, 92, 246, 0.24);
    color: #ddd6fe;
    font-size: 11px;
  }

  .agenda-mobile-list-events {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 10px 12px;
  }

  .agenda-mobile-event {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(28px, auto);
    gap: 2px 10px;
    align-items: center;
    width: 100%;
    padding: 10px;
    border: 1px solid rgba(96, 165, 250, 0.3);
    border-left: 4px solid #60a5fa;
    border-radius: 10px;
    background: rgba(59, 130, 246, 0.12);
    color: #dbeafe;
    text-align: left;
    cursor: pointer;
  }

  .agenda-mobile-event--published {
    border-color: rgba(74, 222, 128, 0.3);
    border-left-color: #4ade80;
    background: rgba(34, 197, 94, 0.1);
    color: #dcfce7;
  }

  .agenda-mobile-event-time {
    grid-row: span 2;
    color: currentColor;
    font-size: 12px;
    font-weight: 700;
  }

  .agenda-mobile-event-title {
    overflow: hidden;
    font-size: 13px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  .agenda-mobile-empty,
  .agenda-mobile-day-detail-empty {
    margin: 0;
    padding: 24px 16px;
    color: var(--clipr-text-muted, #94a3b8);
    font-size: 13px;
    text-align: center;
  }

  .agenda-mobile-empty i {
    display: block;
    margin-bottom: 8px;
    color: #a78bfa;
    font-size: 20px;
  }

  .agenda-mobile-month-weekdays,
  .agenda-mobile-month-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .agenda-mobile-month-weekdays {
    border-bottom: 1px solid rgba(148, 163, 184, 0.14);
    background: rgba(15, 23, 42, 0.7);
  }

  .agenda-mobile-month-weekdays span {
    padding: 9px 1px;
    color: var(--clipr-text-muted, #94a3b8);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-align: center;
  }

  .agenda-mobile-month-day {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 54px;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    gap: 4px;
    padding: 6px 2px;
    border: 0;
    border-right: 1px solid rgba(148, 163, 184, 0.12);
    border-bottom: 1px solid rgba(148, 163, 184, 0.12);
    background: rgba(30, 41, 59, 0.36);
    color: #e2e8f0;
    cursor: pointer;
  }

  .agenda-mobile-month-day:nth-child(7n) {
    border-right: 0;
  }

  .agenda-mobile-month-day:nth-last-child(-n + 7) {
    border-bottom: 0;
  }

  .agenda-mobile-month-day.is-outside-month {
    color: #64748b;
    background: rgba(15, 23, 42, 0.28);
  }

  .agenda-mobile-month-day.is-selected {
    box-shadow: inset 0 0 0 2px rgba(167, 139, 250, 0.76);
  }

  .agenda-mobile-month-day.is-today .agenda-mobile-month-day-number {
    background: #8b5cf6;
    color: #fff;
  }

  .agenda-mobile-month-day-number {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
  }

  .agenda-mobile-month-day-events {
    display: inline-flex;
    min-height: 7px;
    align-items: center;
    justify-content: center;
    gap: 2px;
  }

  .agenda-mobile-month-day-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #60a5fa;
  }

  .agenda-mobile-month-day-dot.is-published {
    width: 8px;
    background: #4ade80;
  }

  .agenda-mobile-month-day-count {
    color: #c4b5fd;
    font-size: 8px;
    font-weight: 700;
  }

  .agenda-mobile-day-detail {
    border-top: 1px solid rgba(148, 163, 184, 0.16);
    background: rgba(15, 23, 42, 0.5);
  }

  .agenda-weekdays span {
    padding: 10px 2px;
    font-size: 9px;
  }

  .agenda-day {
    min-height: 92px;
    padding: 5px 3px;
  }

  .agenda-day-number {
    width: 22px;
    height: 22px;
    font-size: 12px;
  }

  .agenda-day-month {
    font-size: 8px;
  }

  .agenda-day-events {
    margin-top: 4px;
  }

  .agenda-event {
    grid-template-columns: minmax(0, 1fr) auto minmax(28px, auto);
    padding: 3px;
    font-size: 9px;
  }
}
</style>
